import { GENERATE_IMAGE } from 'approot/dictionary/dictionary.graphql';
import type {
  GenerateImageQuery,
  GenerateImageQueryVariables,
} from '../../../../dictionary/dictionary.graphql.generated';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import { ImageThumbnailStatic } from 'approot/shared/images/images';
import { FormExample } from './vocabulary-setting.constants';
import { ExampleImageModal } from './vocabulary-setting-shared';
import { validateHasImageData } from './vocabulary-setting-form.utils';
import * as Styled from './vocabulary-setting-form.style';
import { LanguageCodes } from 'global/constants';

type Props = {
  item: FormExample;
  index: number;
  language?: LanguageCodes;
  word: string;
  canQuery: boolean;
  isViewOnly?: boolean;
  onSetImage: (example: FormExample) => void;
};

const VocabularySettingImage = ({
  item,
  index,
  language,
  word,
  canQuery,
  isViewOnly,
  onSetImage,
}: Props) => {
  const {
    isVisible: isImageUploadVisible,
    openModal: openImageUploadModal,
    closeModal: closeImageUploadModal,
  } = useSimpleModalState();

  const { loading } = useLoggingQuery<
    GenerateImageQuery,
    GenerateImageQueryVariables
  >(GENERATE_IMAGE, {
    fetchPolicy: 'no-cache',
    skip: !canQuery || item.skipGeneratingImage,
    variables: {
      input: {
        sentence: item.example,
        word,
      },
    },
    onError: () => {
      onSetImage({
        ...item,
        image: undefined,
      });
    },
    onCompleted: res => {
      onSetImage({
        ...item,
        image: {
          key: res.generateImage.key,
          fileName: res.generateImage.key,
          cropData: undefined,
          alt: undefined,
          original: res.generateImage.url,
          imageUrl: res.generateImage.url,
        },
      });
    },
  });

  return (
    <>
      {isImageUploadVisible && (
        <ExampleImageModal
          index={index}
          imageData={item.image}
          language={language}
          isViewOnly={isViewOnly}
          onConfirm={imageData => {
            item.image = imageData;
            item.error = validateHasImageData(item);
            onSetImage(item);
          }}
          onClose={closeImageUploadModal}
          word={word}
          example={item.example}
        />
      )}
      <Styled.VocabularyDefinitionImage
        type="button"
        disabled={loading}
        $isLoading={loading}
        onClick={openImageUploadModal}
        data-selector="definition-image"
      >
        {item.image?.imageUrl ? (
          <ImageThumbnailStatic imageUrl={item.image.imageUrl!} width={64} />
        ) : (
          <InlineSvg id="image" width={24} height={24} />
        )}
      </Styled.VocabularyDefinitionImage>
    </>
  );
};

export default VocabularySettingImage;
